import {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import Button from "../../../../components/Button.jsx";
import useDownloadState from "../../../../api/utilities/useDownloadState.js";
import LoadingComponent from "../../../../components/LoadingComponent.jsx";
import {GlobalContext} from "../../../../utils/context/GlobalContextProvider.jsx";
import {ProjectIntegrationsApi} from "../../../../api/authority/generated/index";
import Select from "react-select";
import moment from "moment-timezone";
import {createTimeZoneLabel} from "../../../../utils/timeZone.js";
import toast from "react-hot-toast";
import {useNavigate} from "react-router-dom";
import {useErrorTranslation} from "../../../../api/utilities/translateErrorMessage.js";
import StoredSecretInput from "../IntegrationFormInput/StoredSecretInput.jsx";

function MySQLLeadIntegrationFormInput({type, label, value, note, onChange, secretConfigured = false}) {

    let [id] = useState((Math.random() + 1).toString(36).substring(7));

    return <div>

        <div className={'flex gap-4'}>
            <label className='w-4/12 my-auto text-right' htmlFor={'mysql-lead-' + id}>
                {label}:
            </label>
            <div className={'w-8/12'}>
            {type === 'password' ? <StoredSecretInput
                configured={secretConfigured}
                value={value}
                onChange={onChange}
                concealWhileEditing={true}
            /> : <input
                type={type}
                id={'mysql-lead-' + id}
                className='w-full form-input'
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />}
            </div>
        </div>
        <div className={'flex gap-4'}>
            <div className={'w-4/12'}></div>
            <div className={'w-8/12'}>{type === 'password' ? null : note}</div>
        </div>
    </div>;
}

export default function MySQLLeadIntegrationForm({project, projectIntegration}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();
    let navigate = useNavigate();

    let [mySQLSettings, setMySQLSettings] = useState({
        'hostname': projectIntegration?.hostname ?? '',
        'port': projectIntegration?.port ?? '3306',
        'username': projectIntegration?.username ?? '',
        'password': '',

        'databaseName': projectIntegration?.databaseName ?? '',
        'tableName': projectIntegration?.tableName ?? '',

        'domain': projectIntegration?.domain ?? '',

        'customWhere': projectIntegration?.customWhere ?? '',
        'categoryColumns': projectIntegration?.categoryColumns ?? '',

        'columnDate': projectIntegration?.columnDate ?? '',
        'timeZone': moment.tz.guess(),

        'columnCountry': projectIntegration?.columnCountry,
        'columnVerified': projectIntegration?.columnVerified,

        'columnUtmSource': projectIntegration?.columnUtmSource,
        'columnUtmMedium': projectIntegration?.columnUtmMedium,
        'columnUtmContent': projectIntegration?.columnUtmContent,
        'columnClickId': projectIntegration?.columnClickId,
        'columnUtmCampaign': projectIntegration?.columnUtmCampaign,
        'columnUtmCampaignId': projectIntegration?.columnUtmCampaignId,
        'columnUtmAdSetId': projectIntegration?.columnUtmAdSetId,
        'columnUtmAdSetAdId': projectIntegration?.columnUtmAdSetAdId,
    });

    let {getAuthorityConfiguration, resetCurrentUserProjects} = useContext(GlobalContext);

    let [buttonClicked, setButtonClicked] = useState(false);
    let [state, setDownloading, setResult, setError] = useDownloadState();

    useEffect(() => {

        if (!buttonClicked) {
            return;
        }

        setButtonClicked(false);

        setDownloading(true);

        new ProjectIntegrationsApi(getAuthorityConfiguration())
            .mySQLLeadIntegration({
                projectId: project.id,
                integrationId: projectIntegration?.id ?? null,
                mySQLLeadIntegrationInput: mySQLSettings,
            })
            .then((result) => {
                navigate('/project/' + project.id + '/settings/integrations?integrationId=' + result.id);
                resetCurrentUserProjects();

                if (projectIntegration === null) {
                    toast.success(t('created'));
                } else {
                    if (result.errors.length > 0) {
                        toast.success(t('saved-with-errors'));
                    } else {
                        toast.success(t('saved'));
                    }
                }


            })
            .catch(e => {
                et(e).then((message) => { if (message) toast.error(message); });
            })
            .finally(() => setDownloading(false));

    }, [buttonClicked]);

    // noinspection JSUnresolvedReference
    return (<LoadingComponent loading={state.downloading}>
        <div className={'flex flex-col gap-2'}>
            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('mysql.hostname')}
                value={mySQLSettings.hostname}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, hostname: value,
                    };
                })}
            />

            <MySQLLeadIntegrationFormInput
                type={'integer'}
                label={t('port')}
                value={mySQLSettings.port}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, port: value,
                    };
                })}
                note
            />

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('auth.user')}
                value={mySQLSettings.username}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, username: value,
                    };
                })}
            />


            <MySQLLeadIntegrationFormInput
                type={'password'}
                label={t('auth.password')}
                value={mySQLSettings.password}
                secretConfigured={projectIntegration?.passwordConfigured ?? false}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, password: value,
                    };
                })}
            />

            <div className={'min-h-4 max-h-4'}>{/* spacer */}</div>

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('database.database-name')}
                value={mySQLSettings.databaseName}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, databaseName: value,
                    };
                })}
            />

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('database.table-name')}
                value={mySQLSettings.tableName}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, tableName: value,
                    };
                })}
            />

            <div className={'min-h-4 max-h-4'}>{/* spacer */}</div>


            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('database.domain')}
                value={mySQLSettings.domain}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, domain: value,
                    };
                })}
            />

            <div className={'min-h-4 max-Brh-4'}>{/* spacer */}</div>

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('database.category-columns-comma-separated')}
                value={mySQLSettings.categoryColumns}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, categoryColumns: value,
                    };
                })}
            />

            <div className={'min-h-4 max-Brh-4'}>{/* spacer */}</div>

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('project.mysql-column-date')}
                value={mySQLSettings.columnDate}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, columnDate: value,
                    };
                })}
            />

            <div className={'flex gap-4'}>
                <label className='w-4/12 my-auto text-right' htmlFor={'mysql-lead-date-timezone'}>
                    {t('timezone')}:
                </label>
                <div className='w-8/12'>
                    <Select
                        id={'mysql-lead-date-timezone'}
                        options={moment.tz.names().map((key) => {
                            return {value: key, label: createTimeZoneLabel(key)}
                        })}
                        noOptionsMessage={() => t('project.integration-no-options')}
                        defaultValue={{value: moment.tz.guess(), label: createTimeZoneLabel(moment.tz.guess())}}
                        onChange={(format) => setMySQLSettings((mySQLSettings) => {
                            // noinspection JSUnresolvedReference
                            return {
                                ...mySQLSettings, timeZone: format.value,
                            };
                        })}
                    />
                </div>
            </div>


            <div className={'min-h-4 max-h-4'}>{/* spacer */}</div>

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('project.mysql-column-country')}
                value={mySQLSettings.columnCountry}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, columnCountry: value,
                    };
                })}
            />

            <div className={'min-h-4 max-h-4'}>{/* spacer */}</div>

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('project.mysql-column-verified')}
                note={t('project.mysql-column-verified-values-note')}
                value={mySQLSettings.columnVerified}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, columnVerified: value,
                    };
                })}
            />

            <div className={'min-h-4 max-h-4'}>{/* spacer */}</div>

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('project.mysql-column-utm-source')}
                value={mySQLSettings.columnUtmSource}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, columnUtmSource: value,
                    };
                })}
            />

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('project.mysql-column-utm-medium')}
                value={mySQLSettings.columnUtmMedium}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, columnUtmMedium: value,
                    };
                })}
            />

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('project.mysql-column-utm-content')}
                value={mySQLSettings.columnUtmContent}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, columnUtmContent: value,
                    };
                })}
            />

            <div className={'min-h-4 max-h-4'}>{/* spacer */}</div>

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('project.mysql-column-click-id')}
                value={mySQLSettings.columnClickId}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, columnClickId: value,
                    };
                })}
            />

            <div className={'min-h-4 max-h-4'}>{/* spacer */}</div>

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('project.mysql-column-utm-campaign')}
                value={mySQLSettings.columnUtmCampaign}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, columnUtmCampaign: value,
                    };
                })}
            />

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('project.mysql-column-utm-campaign-id')}
                value={mySQLSettings.columnUtmCampaignId}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, columnUtmCampaignId: value,
                    };
                })}
            />

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('project.mysql-column-utm-ad-set-id')}
                value={mySQLSettings.columnUtmAdSetId}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, columnUtmAdSetId: value,
                    };
                })}
            />

            <MySQLLeadIntegrationFormInput
                type={'text'}
                label={t('project.mysql-column-utm-ad-set-ad-id')}
                value={mySQLSettings.columnUtmAdSetAdId}
                onChange={(value) => setMySQLSettings((mySQLSettings) => {
                    return {
                        ...mySQLSettings, columnUtmAdSetAdId: value,
                    };
                })}
            />

            <div className={'min-h-4 max-h-4'}>{/* spacer */}</div>

            <div className={'flex flex-col p-2 border-1 border-red-600 rounded'}>
                <b className={'text-red-500'}>{t('project.mysql-custom-where-with-warning')}:</b>
                <textarea
                    value={mySQLSettings.customWhere}
                    onChange={(event) => setMySQLSettings((mySQLSettings) => {
                        return {
                            ...mySQLSettings, customWhere: event.target.value,
                        };
                    })}
                ></textarea>
            </div>

            <div className={'min-h-4 max-h-4'}>{/* spacer */}</div>

            <div className={'flex justify-end'}>
                <Button
                    className={'button-primary'}
                    onClick={() => setButtonClicked(true)}
                    title={projectIntegration ? 'Uložit změny' : 'Vytvořit integraci'}
                >
                    {projectIntegration ? 'Uložit změny' : 'Vytvořit integraci'}
                </Button>
            </div>


        </div>
    </LoadingComponent>);

}
